import { useEffect, useMemo, useState } from 'react';
import { Capacitor } from '@capacitor/core';
import { useLocation } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { env } from '@/lib/env';
import {
  getTrackingConsentStatus,
  TRACKING_CONSENT_EVENT,
  type TrackingConsentStatus,
} from '@/utils/trackingManager';

const CHARLA_WIDGET_SCRIPT_SRC = 'https://app.charla.com/widget/widget.js';
const CHARLA_WIDGET_ELEMENT_ID = 'jp-charla-widget';
const CHARLA_WIDGET_SCRIPT_ID = 'jp-charla-widget-script';
const JOURNAL_PARTY_CHARLA_PROPERTY_KEY = '70a1b557-124d-48b1-9d2b-375218c84876';
const CHARLA_PREFIX_SUPPRESSED_PATHS = [
  // Conversion and SEO landing pages: the Help bubble pulls attention from
  // the page's own call to action and sits on top of it on small screens.
  // Pricing, account, settings, billing, help and program pages keep it.
  // Covers the live funnel, including the legacy live challenge funnel.
  '/journal-party-live',
  // Public event pages (/e/:eventId).
  '/e',
  // The prompt hub and every theme, month and persona page under it.
  '/journal-prompts',
  '/faq',
  '/post',
  // Journey funnels (/journal/<seoSlug> and their steps), same as the Challenge.
  '/journal',
  // Session players lock the page to a full-screen 100dvh frame; the floating
  // launcher would sit on top of the timer and sound controls.
  '/session',
  '/daily-prompt/session',
  '/freestyle',
];
const CHARLA_EXACT_SUPPRESSED_PATHS = [
  // Landing page and the Journal Party LIVE page.
  '/',
  '/live',
  // SEO article pages (see the SEO routes in App.tsx).
  '/daily-journal-prompts',
  '/what-to-write-in-a-journal',
  '/journal-prompt-of-the-day',
  '/journal-prompt-generator',
  '/guided-journaling',
  '/guided-journaling-app',
  '/private-journaling-app',
  '/ai-journaling-alternative',
  '/therapist-reviewed-journal-prompts',
  '/best-guided-journaling-platform',
  '/best-journaling-apps',
  '/best-journaling-app-with-prompts',
  '/journal-prompts-for-anxiety',
  '/journal-prompts-for-mental-health',
  '/journal-prompts-for-self-growth',
  '/journaling-for-anxiety',
  '/journaling-for-depression',
  '/gratitude-journaling',
  '/things-to-be-grateful-for',
  '/gratitude-journal-app',
  '/journaling-techniques',
  '/journaling-ideas',
  '/group-journaling',
  '/journaling-club',
  '/journal-sessions',
  '/journaling-challenge',
  '/365-daily-journal-prompts',
  // Keep the focused cold-traffic landing page free of floating controls that
  // can overlap offer details on small screens.
  '/start',
  // /free is a short landing hero. On a 390x664 screen the launcher sits on
  // the hero. Same treatment as /start, and only this path.
  '/free',
  '/auth/callback',
  '/auth/confirm-email',
  '/auth/native-callback',
  '/auth/verify',
  '/challenge',
  '/challenge/premium-offer',
  '/challenge/premium-downsell',
  '/challenge/confirmation',
  // Onboarding is a full-screen native-style takeover; the floating support
  // launcher must not sit on top of its locked frame.
  '/onboarding',
];

function getCharlaPropertyKey(): string {
  const configuredKey = env.charlaPropertyKey;
  return typeof configuredKey === 'string' && configuredKey.trim()
    ? configuredKey.trim()
    : JOURNAL_PARTY_CHARLA_PROPERTY_KEY;
}

function shouldLoadOnLocalhost(): boolean {
  return env.charlaEnableLocal === 'true';
}

function isLocalhost(): boolean {
  if (typeof window === 'undefined') return false;
  return window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
}

export function shouldSuppressCharla(pathname: string): boolean {
  const normalizedPath = (pathname.replace(/\/+$/, '') || '/').toLowerCase();
  if (CHARLA_EXACT_SUPPRESSED_PATHS.includes(normalizedPath)) return true;
  return CHARLA_PREFIX_SUPPRESSED_PATHS.some(
    (path) => normalizedPath === path || normalizedPath.startsWith(`${path}/`)
  );
}

export function shouldDeferCharlaForConsent(status: TrackingConsentStatus): boolean {
  return status === 'pending';
}

const CHARLA_LAUNCHER_STYLE_ID = 'jp-charla-launcher-style';
const CHARLA_CLEARANCE_ATTR = 'data-jp-charla-clearance';
const CHARLA_CLEARANCE_VAR = '--jp-charla-clearance';
/*
 * Charla renders its floating launcher (.widget) inside the open shadow root
 * of <charla-icon>, so page CSS in index.css cannot reach it. These rules are
 * injected into that shadow root once it exists:
 * - z-index 45 keeps the launcher above the mobile bottom nav and sticky CTA
 *   bars (z-40) but below app overlays (z-50 dialogs, bottom sheets, the
 *   sound modal, the profile save bar), so overlays cover the launcher
 *   instead of the launcher floating over them.
 * - Below the lg breakpoint the launcher lifts clear of whatever is pinned to
 *   the viewport bottom: --jp-charla-clearance carries the measured height of
 *   the tallest [data-jp-charla-clearance] element (bottom nav, sticky CTA
 *   bars; custom properties inherit into shadow DOM), and the bottom-nav
 *   height formula stays as the floor in case measurement never ran.
 * The opened chat window lives in <charla-chat> and stays untouched: it is a
 * deliberate full-screen takeover with its own close button.
 */
const CHARLA_LAUNCHER_SHADOW_CSS = `
  .widget {
    z-index: 45 !important;
  }
  @media (max-width: 1023px) {
    .widget {
      bottom: calc(
        max(
          var(${CHARLA_CLEARANCE_VAR}, 0px),
          4rem + max(env(safe-area-inset-bottom), 0.5rem)
        ) + 1rem
      ) !important;
    }
  }
`;

function measureCharlaClearance(): number {
  let clearance = 0;
  const viewportHeight = window.innerHeight;
  document.querySelectorAll(`[${CHARLA_CLEARANCE_ATTR}]`).forEach((el) => {
    const rect = el.getBoundingClientRect();
    const pinnedToBottom = rect.bottom >= viewportHeight - 1;
    if (rect.height > 0 && rect.width > 0 && pinnedToBottom) {
      clearance = Math.max(clearance, viewportHeight - rect.top);
    }
  });
  return Math.round(clearance);
}

function injectCharlaLauncherStyles(): boolean {
  const shadowRoot = document.querySelector('charla-icon')?.shadowRoot;
  if (!shadowRoot) return false;
  if (!shadowRoot.getElementById(CHARLA_LAUNCHER_STYLE_ID)) {
    const style = document.createElement('style');
    style.id = CHARLA_LAUNCHER_STYLE_ID;
    style.textContent = CHARLA_LAUNCHER_SHADOW_CSS;
    shadowRoot.appendChild(style);
  }
  return true;
}

function ensureCharlaScript(): void {
  if (document.getElementById(CHARLA_WIDGET_SCRIPT_ID)) return;

  const widgetCode = document.createElement('script');
  widgetCode.id = CHARLA_WIDGET_SCRIPT_ID;
  widgetCode.src = CHARLA_WIDGET_SCRIPT_SRC;
  widgetCode.async = true;
  document.body.appendChild(widgetCode);
}

export function CharlaWidget() {
  const { user, profile } = useAuth();
  const { pathname } = useLocation();
  // Charla bootstraps its host once, so ordinary route changes must preserve it.
  const isSuppressedRoute = shouldSuppressCharla(pathname);
  const isNativeRuntime = useMemo(() => Capacitor.isNativePlatform(), []);
  const propertyKey = getCharlaPropertyKey();
  const [consentStatus, setConsentStatus] = useState<TrackingConsentStatus>(() => {
    if (typeof window === 'undefined') return 'pending';
    return getTrackingConsentStatus();
  });

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const syncConsent = () => {
      setConsentStatus(getTrackingConsentStatus());
    };

    syncConsent();
    window.addEventListener(TRACKING_CONSENT_EVENT, syncConsent);
    window.addEventListener('storage', syncConsent);

    return () => {
      window.removeEventListener(TRACKING_CONSENT_EVENT, syncConsent);
      window.removeEventListener('storage', syncConsent);
    };
  }, []);

  useEffect(() => {
    if (typeof document === 'undefined') return;
    if (!propertyKey || isNativeRuntime) return;
    if (isLocalhost() && !shouldLoadOnLocalhost()) return;

    if (isSuppressedRoute || shouldDeferCharlaForConsent(consentStatus)) {
      document.getElementById(CHARLA_WIDGET_ELEMENT_ID)?.remove();
      return;
    }

    document.getElementById(CHARLA_WIDGET_ELEMENT_ID)?.remove();

    const customerName =
      profile?.display_name ||
      profile?.full_name ||
      [profile?.first_name, profile?.last_name].filter(Boolean).join(' ') ||
      undefined;

    const widgetElement = document.createElement('charla-widget');
    widgetElement.id = CHARLA_WIDGET_ELEMENT_ID;
    widgetElement.setAttribute('p', propertyKey);
    widgetElement.setAttribute(
      'wc_customer',
      JSON.stringify({
        id: user?.id ?? 0,
        email: user?.email ?? '',
        ...(customerName ? { name: customerName } : {}),
      })
    );
    widgetElement.setAttribute('wc_cart', JSON.stringify([]));

    document.body.appendChild(widgetElement);
    ensureCharlaScript();

    // The shadow root appears when the vendor script boots the widget,
    // which can be well after the script tag loads. Poll briefly and
    // stop as soon as the styles are in (or give up after ~30s).
    let styleAttempts = 0;
    const styleInterval = window.setInterval(() => {
      styleAttempts += 1;
      if (injectCharlaLauncherStyles() || styleAttempts >= 120) {
        window.clearInterval(styleInterval);
      }
    }, 250);
    injectCharlaLauncherStyles();

    // Track bottom-pinned UI (bottom nav, sticky CTA bars) so the launcher
    // clears the tallest one. Bars can mount late (e.g. the pricing CTA waits
    // on the subscription query) and change height (support text wrapping),
    // so watch DOM mutations and element resizes, not just route changes.
    const updateClearance = () => {
      document.documentElement.style.setProperty(
        CHARLA_CLEARANCE_VAR,
        `${measureCharlaClearance()}px`
      );
    };
    const resizeObserver =
      typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(updateClearance);
    const rescanClearance = () => {
      resizeObserver?.disconnect();
      document
        .querySelectorAll(`[${CHARLA_CLEARANCE_ATTR}]`)
        .forEach((el) => resizeObserver?.observe(el));
      updateClearance();
    };
    // rAF only debounces mutation storms; the initial scan runs synchronously
    // because rAF callbacks are paused entirely in hidden/background tabs.
    let clearanceFrame = 0;
    const scheduleClearanceRescan = () => {
      if (clearanceFrame) return;
      clearanceFrame = window.requestAnimationFrame(() => {
        clearanceFrame = 0;
        rescanClearance();
      });
    };
    const mutationObserver = new MutationObserver(scheduleClearanceRescan);
    mutationObserver.observe(document.body, { childList: true, subtree: true });
    window.addEventListener('resize', updateClearance);
    rescanClearance();

    return () => {
      window.clearInterval(styleInterval);
      window.cancelAnimationFrame(clearanceFrame);
      mutationObserver.disconnect();
      resizeObserver?.disconnect();
      window.removeEventListener('resize', updateClearance);
      document.getElementById(CHARLA_WIDGET_ELEMENT_ID)?.remove();
    };
  }, [
    isNativeRuntime,
    profile?.display_name,
    profile?.first_name,
    profile?.full_name,
    profile?.last_name,
    propertyKey,
    isSuppressedRoute,
    consentStatus,
    user?.email,
    user?.id,
  ]);

  return null;
}

export default CharlaWidget;
